import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PenLine, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left panel */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 flex-col justify-between p-12">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-blue-600 rounded-sm flex items-center justify-center">
            <PenLine size={18} className="text-white" />
          </div>
          <span className="text-white font-semibold text-xl" style={{ fontFamily: 'Outfit' }}>SignatureFlow</span>
        </div>
        <div>
          <h2 className="text-white text-3xl font-semibold leading-snug mb-4" style={{ fontFamily: 'Outfit' }}>
            Enterprise-grade<br />e-signatures & workflows
          </h2>
          <p className="text-slate-400 text-base leading-relaxed">
            Securely send, sign, track, and manage legally binding documents. Trusted by legal, HR, finance and procurement teams.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[['100%', 'Legally Binding'], ['SHA-256', 'Document Integrity'], ['Real-time', 'Audit Trail'], ['Multi-party', 'Signing Workflows']].map(([stat, label]) => (
              <div key={label} className="border border-slate-700 rounded-sm p-4">
                <div className="text-blue-400 font-bold text-lg" style={{ fontFamily: 'Outfit' }}>{stat}</div>
                <div className="text-slate-400 text-sm">{label}</div>
              </div>
            ))}
          </div>
        </div>
        <p className="text-slate-600 text-sm">© 2025 SignatureFlow. All rights reserved.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-8 h-8 bg-blue-700 rounded-sm flex items-center justify-center">
              <PenLine size={16} className="text-white" />
            </div>
            <span className="font-semibold text-xl text-slate-900" style={{ fontFamily: 'Outfit' }}>SignatureFlow</span>
          </div>

          <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Sign in</h1>
          <p className="text-slate-500 text-sm mb-6">Enter your credentials to continue</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  required
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPwd(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white py-2.5 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Access is by invitation only. Contact your administrator if you need an account.
          </p>
        </div>
      </div>
    </div>
  );
}
